import React, {useEffect, useState} from 'react';
import {
    Box,
    Paper,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    Button,
    MenuItem,
    InputLabel,
    FormControl,
    TablePagination,
    Slide,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {CompanyService, CompanyIndustryService} from '@/services/accountService';
import CompanyDetail from '@/pages/components/company-info/company/CompanyDetail';
import {CompanyPageData, deleteCompany} from "@/services/accountService/companyService";
import {CompanyIndustry} from "@/services/accountService/companyIndustry";
import {SnackbarField, PopoverButton, InputField} from "@/components/common";
import {usePageCrud} from '@/hooks/usePageCrud';
import {DeleteOutline, InfoOutlined} from '@mui/icons-material';
import AnnualList from '@/components/company-info/company/annual/AnnualList';

export default function CompanyList() {
    const theme = useTheme();
    // Paged data container using CompanyPage interface for records
    const [data, setData] = useState<CompanyPageData>({records: [], total: 0, pageNum: 1, pageSize: 25});
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false, message: '', severity: 'success'
    });
    const [filterCompanyName, setFilterCompanyName] = useState('');
    // const [filterCompanyChineseName, setFilterCompanyChineseName] = useState('');
    const [filterIndustry, setFilterIndustry] = useState<number | ''>('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editId, setEditId] = useState<number | undefined>(undefined);
    const [industries, setIndustries] = useState<CompanyIndustry[]>([]);
    const [expandedCompanyId, setExpandedCompanyId] = useState<number | null>(null);

    // Persist filter in sessionStorage to survive refresh / remount
    const SS_KEYS = {
        industryId: 'companyList.filterIndustry'
    } as const;

    // Restore persisted values on mount
    useEffect(() => {
        const savedId = sessionStorage.getItem(SS_KEYS.industryId);
        if (savedId && savedId !== 'null' && savedId !== 'undefined') {
            const num = Number(savedId);
            if (!Number.isNaN(num)) setFilterIndustry(num as number);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Save whenever filter or input changes
    useEffect(() => {
        sessionStorage.setItem(SS_KEYS.industryId, filterIndustry === '' ? '' : String(filterIndustry));
    }, [filterIndustry]);
    // Load industry options once using company selectList
    useEffect(() => {
        (async () => {
            try {
                const res = await CompanyIndustryService.companySelectList();
                let options: CompanyIndustry[] = [];
                if (Array.isArray(res)) {
                    options = res.map((it: any) => ({
                        industryId: it?.industryId ?? it?.id ?? it?.value,
                        industryName: it?.industryName ?? it?.name ?? it?.label,
                    })).filter((o: CompanyIndustry) => o.industryId != null);
                } else if (res && typeof res === 'object') {
                    options = Object.entries(res).map(([k, v]) => ({
                        industryId: Number(k),
                        industryName: String(v as any)
                    }));
                }
                setIndustries(options);
            } catch {
                setIndustries([]);
            }
        })();
    }, []);

    /**
     * 拉取公司列表。
     * @param opts.silent true=无感刷新（保留当前行，不打龙骨）；false/缺省=整表 Skeleton（菜单进入、筛选、翻页）
     */
    const fetchData = async (pageNum = 1, pageSize = 25, opts?: {silent?: boolean}) => {
        const silent = !!opts?.silent;
        // WHY: Detail 返回/删除后列表已在屏上，避免 Skeleton 闪屏
        if (!silent) {
            setLoading(true);
        }
        try {
            const effectiveIndustry: number | undefined = (filterIndustry !== '') ? filterIndustry : undefined;
            const res = await CompanyService.pageCompanies({
                pageNumber: pageNum,
                pageSize,
                pageType: 1,
                companyName: filterCompanyName || undefined,
                industry: effectiveIndustry,
            });
            setData({
                records: res.list,
                total: res.totalRow,
                pageNum: res.pageNumber,
                pageSize: res.pageSize,
            });
            setError(null);
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    // Ensure selected value exists in options; if not, reset to '' to avoid out-of-range
    useEffect(() => {
        if (filterIndustry === '') return;
        const exists = industries.some(i => i.industryId === filterIndustry);
        if (!exists) setFilterIndustry('');
    }, [industries]);

    // 1) Debounce filter changes to avoid fetching on every keystroke
    useEffect(() => {
        const handler = setTimeout(() => {
            fetchData(page + 1, rowsPerPage);
        }, 400);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterCompanyName, filterIndustry]);

    // 2) Immediate fetch when page/rowsPerPage change
    useEffect(() => {
        fetchData(page + 1, rowsPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    const {canRead, canCreate, canRemove} = usePageCrud({
        read: ['company_query', 'company_get'],
        create: 'account:company:create',
        update: 'account:company:update',
        remove: 'account:company:delete',
    });

    const handleRemove = async (id: number) => {
        setLoading(true);
        try {
            await deleteCompany(id);
            setSnackbar({open: true, message: 'Remove Successful!', severity: 'success'});
            // 删除后无感换数；龙骨仅覆盖 delete 请求本身
            await fetchData(page + 1, rowsPerPage, {silent: true});
        } catch (e: any) {
            const msg = e?.message || 'Remove failed!';
            setSnackbar({open: true, message: msg, severity: 'error'});
        } finally {
            setLoading(false);
        }
    };

    return (
        <Slide direction="left" in={true} mountOnEnter unmountOnExit>
            <Box sx={{
                p: 2,
                bgcolor: 'transparent',
                height: 'calc(100vh - 120px)',
                minHeight: '70vh',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between"
                       alignItems={{xs: 'stretch', sm: 'center'}} gap={1} mb={2} flexShrink={0}>
                    <Typography variant="h6" fontWeight={700} color="text.primary">Company Basic
                        Information</Typography>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} alignItems={{xs: 'stretch', sm: 'center'}}>
                        <InputField size="small" label="Company Name" value={filterCompanyName} onChange={e => {
                            setPage(0);
                            setFilterCompanyName(e.target.value);
                        }}/>
                        <InputField
                            label="Industry"
                            select
                            fullWidth
                            placeholder=""
                            value={filterIndustry !== '' && industries.some(i => i.industryId === filterIndustry) ? String(filterIndustry) : ''}
                            onChange={(e) => {
                                const v = e.target.value as string;
                                setPage(0);
                                setFilterIndustry(v === '' ? '' : Number(v));
                            }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {industries.map((o, idx) => (
                                <MenuItem key={o.industryId ?? `ind-${idx}`} value={o.industryId != null ? String(o.industryId) : ''}>
                                    {o.industryName || '-'}
                                </MenuItem>
                            ))}
                        </InputField>
                        {canCreate && (
                        <Button variant="contained" color="secondary"
                                sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                                onClick={() => {
                                    setEditId(undefined);
                                    setDialogOpen(true);
                                }}>Add</Button>
                        )}
                    </Stack>
                </Stack>

                {error && (
                    <Box sx={{color: 'error.main', px: 1, py: 0.5, fontSize: 12, flexShrink: 0}}>{error}</Box>
                )}

                <TableContainer
                    component={Paper}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        boxShadow: theme.shadows[2],
                        bgcolor: 'background.paper',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            minWidth: 80,
                            // Align content-row height with FsAdjustment (BS/PL leaf rows)
                            '& .MuiTableCell-root': {
                                paddingTop: '5px',
                                paddingBottom: '5px',
                            },
                            '& .MuiTableBody-root .MuiTableRow-root': {
                                height: 36,
                            },
                            // Opaque sticky title: cover body rows while scrolling (action.hover is translucent)
                            '& .MuiTableCell-stickyHeader': {
                                bgcolor: 'background.paper',
                                zIndex: 3,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 500}}>Company Name</TableCell>
                                <TableCell sx={{fontWeight: 500}}>Company Chinese Name</TableCell>
                                <TableCell sx={{fontWeight: 500}}>Industry</TableCell>
                                <TableCell sx={{fontWeight: 700, width: 220}}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && [...Array(rowsPerPage).keys()].map((i) => (
                                <TableRow key={i}>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                    <TableCell><Skeleton variant="rounded" height={24}/></TableCell>
                                </TableRow>
                            ))}
                            {!loading && data.records.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">No data</TableCell>
                                </TableRow>
                            )}
                            {!loading && data.records.map((r, idx) => (
                                <React.Fragment key={r.companyId ?? `row-${idx}`}>
                                    <TableRow
                                        hover
                                        sx={{cursor: 'pointer'}}
                                        onClick={() => {
                                            if (r.companyId == null) return;
                                            setExpandedCompanyId(prev => (prev === r.companyId ? null : r.companyId!));
                                        }}
                                    >
                                        <TableCell>{r.companyName || '-'}</TableCell>
                                        <TableCell>{r.companyChineseName || '-'}</TableCell>
                                        <TableCell>{r.industryName || '-'}</TableCell>
                                        <TableCell sx={{py: '5px'}}>
                                            <Stack direction="row" spacing={0.75} alignItems="center"
                                                   onClick={(e) => e.stopPropagation()}>
                                                {canRead && (
                                                <Button
                                                    size="small"
                                                    startIcon={<InfoOutlined sx={{fontSize: '14px !important'}}/>}
                                                    variant="contained"
                                                    color="secondary"
                                                    sx={{
                                                        height: 24,
                                                        minHeight: 24,
                                                        py: 0,
                                                        px: 1,
                                                        fontSize: 12,
                                                        lineHeight: 1.2,
                                                        borderRadius: theme.shape.borderRadius,
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        boxShadow: theme.shadows[1],
                                                        '& .MuiButton-startIcon': {mr: 0.5, ml: 0},
                                                        '&:hover': {boxShadow: theme.shadows[3]},
                                                    }}
                                                    onClick={() => {
                                                        setEditId(r.companyId);
                                                        setDialogOpen(true);
                                                    }}
                                                >
                                                    Detail
                                                </Button>
                                                )}
                                                {canRemove && r.companyId != null && (
                                                    <span onClick={(e) => e.stopPropagation()}>
                                                        <PopoverButton
                                                            nodeId={r.companyId}
                                                            nodeName={r.companyName || ''}
                                                            onRemove={handleRemove}
                                                            theme={theme}
                                                            trigger={
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    startIcon={<DeleteOutline sx={{fontSize: '14px !important'}}/>}
                                                                    sx={{
                                                                        height: 24,
                                                                        minHeight: 24,
                                                                        minWidth: 72,
                                                                        py: 0,
                                                                        px: 1,
                                                                        fontSize: 12,
                                                                        lineHeight: 1.2,
                                                                        textTransform: 'none',
                                                                        fontWeight: 600,
                                                                        borderColor: theme.palette.error.main,
                                                                        color: theme.palette.error.main,
                                                                        '& .MuiButton-startIcon': {mr: 0.5, ml: 0},
                                                                        '&:hover': {
                                                                            borderColor: theme.palette.error.dark,
                                                                            color: theme.palette.error.dark,
                                                                        },
                                                                    }}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            }
                                                        />
                                                    </span>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                    {expandedCompanyId === r.companyId && r.companyId != null && (
                                        <TableRow sx={{height: 'auto !important'}}>
                                            <TableCell colSpan={4} sx={{bgcolor: 'background.default', p: 0, height: 'auto'}}>
                                                <Box sx={{borderTop: `1px solid ${theme.palette.divider}`, pl: '80px'}}>
                                                    <AnnualList companyId={r.companyId}
                                                                title={`Annuals of ${r.companyName || ''}`}/>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={data.total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{flexShrink: 0}}
                />
                <CompanyDetail
                    open={dialogOpen}
                    companyId={editId}
                    onClose={(p) => {
                        setDialogOpen(false);
                        setEditId(undefined);
                        if (p?.message) setSnackbar({
                            open: true,
                            message: p.message,
                            severity: p.severity || 'success'
                        });
                        if (p?.refreshed) void fetchData(page + 1, rowsPerPage, {silent: true});
                    }}
                />
                <SnackbarField
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({...snackbar, open: false})}
                />
            </Box>
        </Slide>
    );
}
